<template>
  <form class="relative" @submit="onSubmit">
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <BaseInnerLoading v-if="userId && isLoading" />
      <BaseCard>
        <h3 class="mb-3 text-sm text-background-fg/70">
          Details
        </h3>
        <div class="space-y-2">
          <FormInputText
            name="firstName"
            label="First Name"
          />
          <FormInputText
            name="lastName"
            label="Last Name"
          />
          <FormInputText name="email" label="Email" />
        </div>
      </BaseCard>
      <div class="flex flex-col space-y-4">
        <BaseCard>
          <h3 class="mb-3 text-sm text-background-fg/70">
            Permissions
          </h3>
          <div class="space-y-6">
            <FormInputSelect
              name="role"
              label="Role"
              :options="roleOptions"
              disable-null
            />
          </div>
        </BaseCard>
      </div>
    </div>
    <div class="flex justify-end">
      <FormSubmitButton
        v-if="!isLoading"
        :is-loading="isSubmitting"
      >
        {{ userId ? "Update" : "Create" }} User
      </FormSubmitButton>
    </div>
  </form>

  <BaseDialog :is-open="isErrorModalOpen">
    <div class="flex flex-col items-center gap-4 p-3">
      <h2 class="text-center text-lg font-semibold"
        >Something went wrong</h2
      >
      <p class="text-center text-sm text-error"
        >Something went wrong fetching user details</p
      >
      <RouterLink :to="{ name: 'userIndex' }">
        <ButtonPrimary>Return to user list</ButtonPrimary>
      </RouterLink>
    </div>
  </BaseDialog>
</template>

<script setup lang="ts">
import {
  type CreateEditUserFormOutput,
  useCreateEditUserForm,
} from "@/composables/forms/useCreateEditUserForm"
import type { RetieveUserResponse } from "@/services/users/retrieveUser"
const router = useRouter()

const emit = defineEmits<{
  (event: "submit", values: CreateEditUserFormOutput): void
}>()

const props = defineProps<{
  userId?: string
}>()

const { handleSubmit, setErrors, resetForm, isSubmitting } =
  useCreateEditUserForm()

const { data: roles } = useRoles()
const isLoading = ref(false)
const isErrorModalOpen = ref(false)
const userData = ref<RetieveUserResponse | null>(null)

onMounted(async () => {
  if (props.userId !== undefined) {
    try {
      isLoading.value = true
      const { id, ...user } = await retrieveUser(
        props.userId
      )
      resetForm({
        values: {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          role: user.role?.name,
        },
      })
      userData.value = { id, ...user }
    } catch {
      isErrorModalOpen.value = true
    } finally {
      isLoading.value = false
    }
  }
})
const onSubmit = handleSubmit(async (formValues) => {
  try {
    if (props.userId) {
      await updateUser(props.userId, { ...formValues })
    } else {
      await createUser(formValues)
    }
    toast.success(
      props.userId ? "User updated" : "User created"
    )
    router.push({ name: "userIndex" })
  } catch (error) {
    if (error instanceof ApiError) {
      setErrors(error.requestErrors?.body ?? {})
    }
    toast.error(
      props.userId
        ? "Failed to update user"
        : "Failed to create user"
    )
    throw error
  }
})

const roleOptions = computed(() =>
  roles.value
    ? roles.value.map((role) => ({
        label: role.name,
        value: role.name,
      }))
    : []
)
</script>
